/**
 * useSpawnBatchBags Hook
 *
 * Provides operations for individual spawn batch bags within a batch.
 * Part of US-P2-002: Spawn Production Tracking (Individual Bag Tracking)
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/supabase-client'

/**
 * Call spawn-batches Edge Function
 * @param {string} action - The action to perform
 * @param {Object} data - Request payload
 * @returns {Promise<Object>} Response data
 */
async function callSpawnBatchesFunction(action, data = {}) {
  const { data: responseData, error } = await supabase.functions.invoke('spawn-batches', {
    body: { action, ...data }
  })

  if (error) {
    throw new Error(error.message || 'Edge function error')
  }

  if (responseData?.error) {
    throw new Error(responseData.message || responseData.error)
  }

  return responseData
}

/**
 * Hook to fetch individual bags for a spawn batch
 * @param {string} spawnBatchId - The spawn batch UUID
 * @param {Object} options - Filter options
 * @param {string} options.status - Filter by bag status (available, used, contaminated, discarded)
 * @returns {Object} { bags, loading, error, refetch }
 */
export function useSpawnBatchBags(spawnBatchId, options = {}) {
  const [bags, setBags] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchBags = useCallback(async () => {
    if (!spawnBatchId) {
      setBags([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await callSpawnBatchesFunction('get-bags', {
        spawn_batch_id: spawnBatchId,
        status: options.status
      })

      setBags(response.data || [])
    } catch (err) {
      console.error('[useSpawnBatchBags] Error:', err)
      setError(err.message)
      setBags([])
    } finally {
      setLoading(false)
    }
  }, [spawnBatchId, options.status])

  useEffect(() => {
    fetchBags()
  }, [fetchBags])

  return {
    bags,
    loading,
    error,
    refetch: fetchBags
  }
}

/**
 * Hook for individual bag mutations (mark colonised, contaminated, discard)
 * @returns {Object} Mutation functions and state
 */
export function useSpawnBagMutations() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Mark a bag as colonised (sets colonised_at timestamp)
   * Uses bag_action: 'colonise' as expected by the Edge Function
   * @param {string} bagId - Bag UUID
   * @returns {Promise<Object>} Updated bag
   */
  const markBagColonised = useCallback(async (bagId) => {
    setLoading(true)
    setError(null)

    try {
      const response = await callSpawnBatchesFunction('update-bag', {
        bag_id: bagId,
        bag_action: 'colonise'
      })
      return response.data
    } catch (err) {
      console.error('[useSpawnBagMutations.markColonised] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Mark a bag as contaminated
   * @param {string} bagId - Bag UUID
   * @param {string} notes - Optional contamination notes
   * @returns {Promise<Object>} Updated bag
   */
  const markBagContaminated = useCallback(async (bagId, notes = '') => {
    setLoading(true)
    setError(null)

    try {
      const response = await callSpawnBatchesFunction('update-bag', {
        bag_id: bagId,
        status: 'contaminated',
        contamination_notes: notes
      })
      return response.data
    } catch (err) {
      console.error('[useSpawnBagMutations.markContaminated] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Mark a bag as discarded
   * @param {string} bagId - Bag UUID
   * @param {string} notes - Optional notes
   * @returns {Promise<Object>} Updated bag
   */
  const discardBag = useCallback(async (bagId, notes = '') => {
    setLoading(true)
    setError(null)

    try {
      const response = await callSpawnBatchesFunction('update-bag', {
        bag_id: bagId,
        status: 'discarded',
        notes
      })
      return response.data
    } catch (err) {
      console.error('[useSpawnBagMutations.discard] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Mark a bag as used (untracked usage - no inoculation event linked)
   * Use this when spawn was used during onboarding without recording it in Odin
   * @param {string} bagId - Bag UUID
   * @param {string} notes - Optional notes explaining why it was marked this way
   * @returns {Promise<Object>} Updated bag
   */
  const markBagUsed = useCallback(async (bagId, notes = '') => {
    setLoading(true)
    setError(null)

    try {
      const response = await callSpawnBatchesFunction('update-bag', {
        bag_id: bagId,
        status: 'used',
        notes
      })
      return response.data
    } catch (err) {
      console.error('[useSpawnBagMutations.markUsed] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    loading,
    error,
    markBagColonised,
    markBagContaminated,
    discardBag,
    markBagUsed
  }
}

export default useSpawnBatchBags
